import React, { useState } from 'react';
import { Modal, Form, Button, Badge, Alert, Row, Col, Card } from 'react-bootstrap';
import { User, Package, Calendar, CheckCircle2, AlertCircle, Save, X } from 'lucide-react';
import { Reserva } from '../../types';
import { updateDevolucaoStatus } from '../../services/firestoreService';
import { formatNumeroReserva } from '../../utils/reservaUtils';

interface DevolucaoModalProps {
    show: boolean;
    onHide: () => void;
    reserva: Reserva | null;
    onUpdate: () => void;
}

const DevolucaoModal: React.FC<DevolucaoModalProps> = ({ show, onHide, reserva, onUpdate }) => {
    const [devolucaoRealizada, setDevolucaoRealizada] = useState(
        reserva?.devolucaoRealizada || false
    );
    const [observacoes, setObservacoes] = useState(
        reserva?.devolucaoObservacoes || ''
    );
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    // Atualizar estado quando reserva mudar
    React.useEffect(() => {
        if (reserva) {
            setDevolucaoRealizada(reserva.devolucaoRealizada || false);
            setObservacoes(reserva.devolucaoObservacoes || '');
            setError(null);
            setSuccess(false);
        }
    }, [reserva]);

    const handleSave = async () => {
        if (!reserva?.id) return;

        setSaving(true);
        setError(null);
        setSuccess(false);

        try {
            const result = await updateDevolucaoStatus(
                reserva.id,
                devolucaoRealizada,
                observacoes
            );

            if (result.success) {
                setSuccess(true);
                setTimeout(() => {
                    setSuccess(false);
                    onUpdate();
                    onHide();
                }, 1500);
            } else {
                setError(result.error || 'Erro ao salvar');
            }
        } catch (err: any) {
            setError(err.message || 'Erro ao salvar');
        } finally {
            setSaving(false);
        }
    };

    // Verificar se houve mudanças
    const hasChanges =
        devolucaoRealizada !== (reserva?.devolucaoRealizada || false) ||
        observacoes !== (reserva?.devolucaoObservacoes || '');

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('pt-BR');
    };

    if (!reserva) return null;

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="d-flex align-items-center gap-2">
                    <Package size={24} className="text-primary" />
                    <div>
                        <div className="fw-bold">Gerenciar Devolução</div>
                        <small className="text-muted">
                            Reserva {formatNumeroReserva(reserva.numeroReserva || '')}
                        </small>
                    </div>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {/* Header com informações do cliente */}
                <Row className="mb-4">
                    <Col xs={12}>
                        <Card className="border-0 bg-light">
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            <User size={20} className="text-primary" />
                                            <span className="fw-bold">{reserva.clienteNome}</span>
                                        </div>
                                        <div className="d-flex flex-column gap-2 text-muted small">
                                            <div className="d-flex align-items-center gap-2">
                                                <Calendar size={16} className="text-primary" />
                                                <span>
                                                    <strong>Retirada:</strong> {formatDate(reserva.dataRetirada)}
                                                </span>
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                                <Calendar size={16} className="text-danger" />
                                                <span>
                                                    <strong>Devolução Prevista:</strong> {formatDate(reserva.dataDevolucao)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <Badge
                                        bg={devolucaoRealizada ? 'success' : 'warning'}
                                        className="status-badge"
                                    >
                                        {devolucaoRealizada ? (
                                            <>
                                                <CheckCircle2 size={14} className="me-1" />
                                                Devolvido
                                            </>
                                        ) : (
                                            <>
                                                <AlertCircle size={14} className="me-1" />
                                                Pendente
                                            </>
                                        )}
                                    </Badge>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Produtos */}
                <Row className="mb-4">
                    <Col xs={12}>
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <Package size={18} className="text-primary" />
                            <span className="fw-bold text-dark">Produtos ({reserva.produtos.length})</span>
                        </div>
                        <div className="border rounded p-3 bg-light">
                            {reserva.produtos.map((produto, index) => (
                                <div key={index} className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom">
                                    <div className="flex-grow-1">
                                        <div className="fw-semibold">{produto.produtoTitulo}</div>
                                        <div className="small text-muted">
                                            {produto.produtoCodigoProduto && `[${produto.produtoCodigoProduto}] `}
                                            {produto.produtoTamanho} • {produto.produtoCor}
                                        </div>
                                    </div>
                                    <Badge bg="primary" pill style={{ padding: '0.4rem 0.8rem' }}>
                                        {produto.quantidade}x
                                    </Badge>
                                </div>
                            ))}
                        </div>
                    </Col>
                </Row>

                {/* Checkbox de devolução */}
                <Form.Check
                    type="checkbox"
                    id={`devolvido-${reserva.id}`}
                    label="Cliente devolveu os produtos"
                    checked={devolucaoRealizada}
                    onChange={(e) => setDevolucaoRealizada(e.target.checked)}
                    className="devolucao-checkbox mb-3"
                />

                {/* Campo de observações */}
                <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">
                        Observações
                        <span className="text-muted ms-2" style={{ fontSize: '0.85rem' }}>
                            (peças faltantes, esquecidas, etc.)
                        </span>
                    </Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Ex: Faltou gravata vermelha, cinto preto danificado..."
                        value={observacoes}
                        onChange={(e) => setObservacoes(e.target.value)}
                        className="observacoes-textarea"
                    />
                </Form.Group>

                {/* Mensagens de feedback */}
                {error && (
                    <Alert variant="danger" className="mb-3 py-2">
                        <AlertCircle size={16} className="me-2" />
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert variant="success" className="mb-3 py-2">
                        <CheckCircle2 size={16} className="me-2" />
                        Salvo com sucesso!
                    </Alert>
                )}

                {/* Data da devolução realizada */}
                {reserva.dataDevolucaoRealizada && (
                    <div className="text-center text-muted mb-3" style={{ fontSize: '0.85rem' }}>
                        Devolvido em: {formatDate(reserva.dataDevolucaoRealizada)}
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer className="border-0 pt-0">
                <Button variant="secondary" onClick={onHide}>
                    <X size={18} className="me-2" />
                    Cancelar
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={!hasChanges || saving}
                >
                    <Save size={18} className="me-2" />
                    {saving ? 'Salvando...' : 'Salvar Alterações'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default DevolucaoModal;

